import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CampaignTrackingService {
  private readonly CAMPAIGN_KEY = 'campaign_ids';
  private readonly UNSUBSCRIBE_SESSION_ID = 'unsubscribe_session_id';

  constructor() {}

  /**
   * Capture campaign_id from URL and store it
   */
  captureCampaignId(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const campaignId = urlParams.get('campaign_id');
    
    if (campaignId) {
      this.storeCampaignId(campaignId);
    }

    const unsubscribeSessionId = urlParams.get('session_id');
    if (unsubscribeSessionId) {
      this.storeUnsubscribeSessionId(unsubscribeSessionId);
    }
  }

  /**
   * Store campaign_id in localStorage, supporting multiple IDs
   */
  private storeCampaignId(campaignId: string): void {
    try {
      const existingIds = this.getCampaignIds();
      
      // Avoid duplicates
      if (!existingIds.includes(campaignId)) {
        existingIds.push(campaignId);
        localStorage.setItem(this.CAMPAIGN_KEY, JSON.stringify(existingIds));
      }
    } catch (error) {
      console.warn('Failed to store campaign ID:', error);
    }
  }

  storeUnsubscribeSessionId(sessionId: string): void {
    try {
      localStorage.setItem(this.UNSUBSCRIBE_SESSION_ID, sessionId);
    } catch (error) {
      console.warn('Failed to store unsubscribe session ID:', error);
    }
  }

  /**
   * Get all stored campaign IDs
   */
  getCampaignIds(): string[] {
    try {
      const stored = localStorage.getItem(this.CAMPAIGN_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to retrieve campaign IDs:', error);
      return [];
    }
  }

  /**
   * Get the most recent campaign ID
   */
  getLatestCampaignId(): string | null {
    const ids = this.getCampaignIds();
    return ids.length > 0 ? ids[ids.length - 1] : null;
  }

  /**
   * Append campaign_id to URL if it exists in storage
   */
  appendCampaignIdToUrl(baseUrl: string): string {
    const campaignId = this.getLatestCampaignId();
    
    if (!campaignId) {
      return baseUrl;
    }

    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('campaign_id', campaignId);
    
    return url.pathname + url.search.toString();
  }

  /**
   * Clear campaign IDs (called after preference update completion)
   */
  clearCampaignIds(): void {
    localStorage.removeItem(this.CAMPAIGN_KEY);
    localStorage.removeItem('preference_updated');
    localStorage.removeItem(this.UNSUBSCRIBE_SESSION_ID);
    
    const url = new URL(window.location.href);
    url.searchParams.delete('campaign_id');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  /**
   * Get campaign parameters for API requests
   */
  getCampaignParams(): { campaign_id?: string } {
    const campaignId = this.getLatestCampaignId();
    return campaignId ? { campaign_id: campaignId } : {};
  }
}